/** Saúde dos jobs, LGPD (retenção/anonimização/exportação) e ciência no portal. */
import { z } from 'zod';
import { Id } from './comum.js';

// ---- saúde dos jobs
export const SaudeJobItem = z.object({ id: z.string(), rotulo: z.string(), ultimoOkEm: z.string().nullable(), ultimoErro: z.string().nullable(), ultimoErroEm: z.string().nullable(), limiteMinutos: z.number().int(), parado: z.boolean(), atrasoMinutos: z.number().int().nullable() });
export const SaudeJobs = z.object({ ok: z.boolean(), jobs: z.array(SaudeJobItem), verificadoEm: z.string() });
export type SaudeJobs = z.infer<typeof SaudeJobs>;

// ---- LGPD
export const RetencaoCandidato = z.object({ id: Id, razaoSocial: z.string(), documento: z.string(), dataEncerramento: z.string().nullable(), anosDesdeBaixa: z.number(), anexos: z.number().int(), contatos: z.number().int() });
export const RetencaoResposta = z.object({ retencaoAnos: z.number().int(), candidatos: z.array(RetencaoCandidato), anonimizados: z.number().int() });
export type RetencaoResposta = z.infer<typeof RetencaoResposta>;
export const AnonimizarResposta = z.object({ contatosAnonimizados: z.number().int(), anexosRemovidos: z.number().int(), sessoesRevogadas: z.number().int(), anonimizadoEm: z.string() });

// ---- documentos ao cliente (e-mail com link)
export const DocumentoCliente = z.object({
  id: Id, nome: z.string(), /** nome padronizado que o cliente vê */ titulo: z.string().optional(), mime: z.string(), tamanho: z.number().int(), criadoEm: z.string(), tarefaId: Id.nullable(), tarefaTitulo: z.string().nullable(), competenciaLabel: z.string().nullable(),
  obrigacaoSigla: z.string().nullable(), departamento: z.string().nullable(), vencimento: z.string().nullable(), exigeCiencia: z.boolean(), enviadoEmailEm: z.string().nullable(), enviadoPorApelido: z.string().nullable(),
  ciencias: z.array(z.object({ contatoNome: z.string(), origem: z.enum(['PORTAL', 'LINK']), quando: z.string(), ip: z.string().nullable(), acessos: z.number().int() })),
  /** já assinado eletronicamente por algum contato */
  assinado: z.boolean().default(false),
  /** é o comprovante de uma entrega registrada na tarefa */
  daEntrega: z.boolean().default(false),
  /** está num envio aberto (na janela): sai sozinho em instantes */
  envioAbertoEm: z.string().nullable().optional(),
  /** guia, recibo (declaração) ou documento — pela entrega de que o arquivo é comprovante */
  papel: z.enum(['GUIA', 'RECIBO', 'DOCUMENTO']).optional(),
});
export type DocumentoCliente = z.infer<typeof DocumentoCliente>;
export const EnviarDocumentosBody = z.object({ anexoIds: z.array(Id).min(1).max(100), mensagem: z.string().trim().max(1000).nullable().default(null) });
export const EnviarDocumentosResposta = z.object({ emails: z.number().int(), contatos: z.number().int(), documentos: z.number().int() });
export const DocumentosAcaoBody = z.object({ anexoIds: z.array(Id).min(1).max(200) });
export const DocumentosPublicarBody = DocumentosAcaoBody.extend({ publicar: z.boolean() });
export const DocumentosExcluidos = z.object({ excluidos: z.number().int(), comCiencia: z.number().int(), entregasDesvinculadas: z.number().int() });
export type DocumentosExcluidos = z.infer<typeof DocumentosExcluidos>;

// ---- ciência
export const CienciaInfo = z.object({ contatoNome: z.string(), quando: z.string(), ip: z.string().nullable() });
export const CienciaBody = z.object({ texto: z.string().trim().min(3).max(300).default('Li e estou ciente do conteúdo deste documento.') });

// ---- modelos (tipos) de documento: o nome padronizado do anexo no e-mail, no portal e no download
export const DocumentoModeloBody = z.object({
  nome: z.string().trim().min(2).max(120),
  /** código da obrigação do catálogo (DAS, FGTS…); nulo = serve a qualquer tarefa */
  obrigacaoCodigo: z.string().trim().max(40).nullable().default(null),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  /** comprovante de que tipo de entrega (guia → GUIA, recibo → DECLARACAO): é como o sistema acha o modelo sozinho */
  tipoEntrega: z.enum(['DECLARACAO', 'GUIA', 'DOCUMENTO']).nullable().default(null),
  /** ao registrar a entrega com este documento: titula, publica e envia o e-mail ao cliente sem ninguém clicar */
  enviarAutomatico: z.boolean().default(false),
  publicar: z.boolean().default(true),
  exigeCiencia: z.boolean().default(false),
  ordem: z.number().int().min(0).max(9999).default(0),
  ativo: z.boolean().default(true),
});
export type DocumentoModeloBody = z.infer<typeof DocumentoModeloBody>;
export const DocumentoModeloEditarBody = z.object({
  nome: z.string().trim().min(2).max(120).optional(), obrigacaoCodigo: z.string().trim().max(40).nullable().optional(), departamentoSigla: z.string().trim().max(8).nullable().optional(),
  publicar: z.boolean().optional(), exigeCiencia: z.boolean().optional(), ordem: z.number().int().min(0).max(9999).optional(), ativo: z.boolean().optional(),
  tipoEntrega: z.enum(['DECLARACAO', 'GUIA', 'DOCUMENTO']).nullable().optional(), enviarAutomatico: z.boolean().optional(),
});
export type DocumentoModeloEditarBody = z.infer<typeof DocumentoModeloEditarBody>;
export const DocumentoModelo = z.object({
  id: Id, nome: z.string(), obrigacaoCodigo: z.string().nullable(), obrigacaoSigla: z.string().nullable(), departamentoSigla: z.string().nullable(),
  publicar: z.boolean(), exigeCiencia: z.boolean(), ordem: z.number().int(), ativo: z.boolean(),
  tipoEntrega: z.enum(['DECLARACAO', 'GUIA', 'DOCUMENTO']).nullable().optional(), enviarAutomatico: z.boolean().optional(),
});
export type DocumentoModelo = z.infer<typeof DocumentoModelo>;
export const DocumentoModelosPadrao = z.object({ criados: z.number().int(), existentes: z.number().int() });
